// /oauth/authorize — the consent screen an AI app sends an owner to when it
// connects to Neomaris's MCP server (TOM-843, ADR 0413 §2; contract §9.5).
// The cloud serves this page at the authorization endpoint itself, after it
// has checked the request and the owner is signed in; RequireAuth sends a
// signed-out owner to /login and back.
//
// The page names the app, the host its answer goes to and what it may do, and
// warns when the app is not one Neomaris knows. Allow and Deny are a
// same-origin POST of the request's own query string; the server checks the
// request again and answers where to send the browser: back to the app with a
// code, or with `error=access_denied`.
//
// OauthConsentView draws every state from props (tested under Node, as the web
// package has no DOM test environment); OauthConsent owns the requests.

import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { api } from '../api';
import { useDocumentTitle } from '../lib/useDocumentTitle';

/** What `GET /v1/mcp/oauth/consent` answers for a good request (contract §9.5). */
export interface ConsentDetails {
  client: { id: string; name: string; verified: boolean; redirectHost: string };
  scopes: string[];
}

interface ApiError {
  status?: number;
  body?: { error?: string; message?: string; redirectTo?: string };
  message?: string;
}

/** Each scope in plain words, in the order the screen lists them. */
export const CONSENT_SCOPES: readonly { scope: string; title: string }[] = [
  { scope: 'boat:read', title: 'Read your boat data' },
  { scope: 'neo:ask', title: 'Ask Neo questions' },
];

/** A scope's plain words; an unknown one shows as itself. */
export function scopeTitle(scope: string): string {
  return CONSENT_SCOPES.find((s) => s.scope === scope)?.title ?? scope;
}

/** The sentence that says what the app gets, across every boat. */
export function consentSummary(name: string, scopes: string[]): string {
  const read = scopes.includes('boat:read');
  const ask = scopes.includes('neo:ask');
  if (read && ask) {
    return `${name} will be able to read your boat data and ask Neo questions, for all your boats.`;
  }
  if (read) return `${name} will be able to read your boat data for all your boats.`;
  if (ask) return `${name} will be able to ask Neo questions about all your boats.`;
  return `${name} will be able to reach your boats.`;
}

/**
 * The name the screen uses for the app (TOM-843): a known app's own name, or
 * an unverified app's self-chosen name quoted and marked, so a client that
 * calls itself "Claude" never reads as Claude without the marker beside it.
 */
export function consentClientName(client: Pick<ConsentDetails['client'], 'name' | 'verified'>) {
  return client.verified ? client.name : `“${client.name}” (unverified)`;
}

/** How a failed read or decision is shown: go back to the app, or a message. */
export function consentFailure(err: ApiError): { redirectTo: string } | { message: string } {
  const to = err.body?.redirectTo;
  if (typeof to === 'string' && to.length > 0) return { redirectTo: to };
  if (err.body?.error === 'browser_session_required') {
    return { message: 'Connect an app from a web browser, signed in to your account.' };
  }
  if (err.body?.error === 'temporarily_unavailable') {
    return { message: 'Too many apps are connecting right now. Go back to the app and try again.' };
  }
  if (err.body?.error === 'invalid_client' || err.body?.error === 'invalid_redirect_uri') {
    return {
      message: `This app can’t connect to Neomaris: ${err.body.message ?? 'its request is not valid'}. Nothing was shared.`,
    };
  }
  return { message: err.body?.message ?? err.message ?? 'Couldn’t read this request.' };
}

export type ConsentStatus = 'loading' | 'ready' | 'deciding' | 'leaving' | 'error';

export interface OauthConsentViewProps {
  status: ConsentStatus;
  details: ConsentDetails | null;
  error: string | null;
  onAllow: () => void;
  onDeny: () => void;
}

export function OauthConsentView({
  status,
  details,
  error,
  onAllow,
  onDeny,
}: OauthConsentViewProps) {
  if (status === 'loading') {
    return (
      <section className="auth-panel">
        <div className="auth-card" data-testid="oauth-consent-loading">
          <p className="auth-sub">Checking the app’s request…</p>
        </div>
      </section>
    );
  }
  if (status === 'error' || details === null) {
    return (
      <section className="auth-panel">
        <div className="auth-card" data-testid="oauth-consent-error">
          <h2>Can’t connect this app</h2>
          <ul className="form-errors">
            <li>{error ?? 'Couldn’t read this request.'}</li>
          </ul>
          <a href="/devices" className="btn auth-submit">
            Back to linked devices
          </a>
        </div>
      </section>
    );
  }
  const { client, scopes } = details;
  const busy = status === 'deciding' || status === 'leaving';
  return (
    <section className="auth-panel">
      <div className="auth-card oauth-consent" data-testid="oauth-consent">
        <h2>
          Connect{' '}
          {client.verified ? (
            <span data-testid="oauth-client-name">{client.name}</span>
          ) : (
            <>
              “<span data-testid="oauth-client-name">{client.name}</span>”{' '}
              <span className="oauth-consent__unverified-tag" data-testid="oauth-unverified-tag">
                (unverified)
              </span>
            </>
          )}
          ?
        </h2>
        <p className="auth-sub">
          It will get its answer at{' '}
          <strong data-testid="oauth-redirect-host">{client.redirectHost}</strong>.
        </p>
        {!client.verified && (
          <div className="oauth-consent__warning" role="note" data-testid="oauth-unverified">
            <strong>Unverified app.</strong> Neomaris hasn’t checked who makes this app. Connect it
            only if you started this from an app you trust and the address above is the one you
            expect.
          </div>
        )}
        <p className="oauth-consent__lead">It will be able to:</p>
        <ul className="oauth-consent__scopes" data-testid="oauth-scopes">
          {scopes.map((s) => (
            <li key={s}>{scopeTitle(s)}</li>
          ))}
        </ul>
        <p className="auth-sub" data-testid="oauth-summary">
          {consentSummary(consentClientName(client), scopes)} It can’t change anything on a boat or
          in your account, and you can disconnect it on Linked devices at any time.
        </p>
        <button
          type="button"
          className="btn btn-primary auth-submit"
          onClick={onAllow}
          disabled={busy}
          data-testid="oauth-allow"
        >
          {status === 'deciding' ? 'Connecting…' : 'Allow'}
        </button>
        <button
          type="button"
          className="btn auth-submit auth-secondary"
          onClick={onDeny}
          disabled={busy}
          data-testid="oauth-deny"
        >
          Deny
        </button>
      </div>
    </section>
  );
}

/** The consent page: reads the request, then sends the owner's choice. */
export function OauthConsent() {
  useDocumentTitle('Connect an app - Neomaris');
  const { search } = useLocation();
  const [status, setStatus] = useState<ConsentStatus>('loading');
  const [details, setDetails] = useState<ConsentDetails | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api<ConsentDetails>(`/v1/mcp/oauth/consent${search}`)
      .then((res) => {
        if (!live) return;
        setDetails(res);
        setStatus('ready');
      })
      .catch((err: unknown) => {
        if (!live) return;
        const failure = consentFailure(err as ApiError);
        if ('redirectTo' in failure) {
          setStatus('leaving');
          window.location.replace(failure.redirectTo);
          return;
        }
        setError(failure.message);
        setStatus('error');
      });
    return () => {
      live = false;
    };
  }, [search]);

  async function decide(decision: 'allow' | 'deny') {
    setStatus('deciding');
    try {
      const res = await api<{ redirectTo: string }>('/v1/mcp/oauth/consent', {
        method: 'POST',
        body: { decision, query: search },
      });
      setStatus('leaving');
      window.location.replace(res.redirectTo);
    } catch (err) {
      const failure = consentFailure(err as ApiError);
      if ('redirectTo' in failure) {
        setStatus('leaving');
        window.location.replace(failure.redirectTo);
        return;
      }
      setError(failure.message);
      setStatus('error');
    }
  }

  return (
    <OauthConsentView
      status={status}
      details={details}
      error={error}
      onAllow={() => void decide('allow')}
      onDeny={() => void decide('deny')}
    />
  );
}
